// MapBarcodeModal.jsx — staff-only (admin + rep): when a scanned barcode
// matches nothing, offer to map it to a product on the spot instead of
// bouncing the user to the Barcode Mapping page. Searches ALL products
// (including inactive — an out-of-stock item still owns its barcode).
// firestore.rules allows reps to write ONLY the barcode field on products.
import { useEffect, useState } from "react";
import { getAllProducts } from "../services/dataCache";
import { assignBarcode } from "../services/barcodeService";
import { useLang } from "../i18n/LanguageContext";

export default function MapBarcodeModal({ code, onClose, onMapped }) {
  const { t } = useLang();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    async function load() {
      setProducts(await getAllProducts()); // session cache — 0 reads on revisit
      setLoading(false);
    }
    load();
  }, []);

  const filtered = search
    ? products.filter(p =>
        (p.name || "").toLowerCase().includes(search.toLowerCase()) ||
        (p.sku || "").includes(search))
    : [];

  async function assign(p) {
    if (saving) return;
    // Overwrite guard (Brian, 2026-07-14): if this product already carries a
    // different barcode, confirm before replacing it.
    if (p.barcode && String(p.barcode) !== String(code)) {
      const ok = window.confirm(
        `${p.sku} — ${p.name}\nalready has barcode ${p.barcode}.\n\nReplace it with ${code}?`
      );
      if (!ok) return;
    }
    setSaving(true);
    setError("");
    try {
      // assignBarcode also strips this code from any OTHER product holding
      // it, so a mis-mapped barcode is truly moved, not duplicated.
      await assignBarcode(p.sku, code, products);
      onMapped({ ...p, barcode: String(code), barcodeSource: "portal" });
    } catch (err) {
      setError(`Could not save mapping: ${err.message}`);
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>
        <h3>{t("unknownBarcode")}</h3>
        <p className="map-barcode-code">{t("scanned")} <strong>{code}</strong></p>
        <p className="inventory-help-note">
          {t("unknownBarcodeHelp")}
        </p>
        {loading ? (
          <p>{t("loadingProducts")}</p>
        ) : (
          <>
            <input
              className="map-barcode-search"
              placeholder={t("searchNameSku")}
              value={search}
              autoFocus
              onChange={(e) => setSearch(e.target.value)}
            />
            {error && <p className="error">{error}</p>}
            <div className="barcode-match-list">
              {filtered.slice(0, 10).map(p => (
                <div key={p.sku} className="barcode-match-row" onClick={() => assign(p)}>
                  <span className="sku">{p.sku}</span>
                  <span>{p.name}</span>
                  {p.barcode && <span className="already-mapped">{t("hasBarcode")}</span>}
                </div>
              ))}
              {search && filtered.length === 0 && (
                <p className="pick-empty">{t("noProductsMatch")}</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
